import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { type ReactNode } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

// Development mode - skip authentication in dev
const isDevelopment = import.meta.env.DEV;

interface ProtectedRouteProps {
  requiredRoles?: string[];
  requiredPermissions?: string[];
  children?: ReactNode;
}

export function ProtectedRoute({ requiredRoles, requiredPermissions, children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasAnyRole, hasAnyPermission } = useAuth();
  const location = useLocation();

  // In development mode, skip authentication check
  if (isDevelopment) {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles && requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredPermissions && requiredPermissions.length > 0 && !hasAnyPermission(requiredPermissions)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (children) {
    return <>{children}</>;
  }

  return <Outlet />;
}
